import React, { useState, useEffect, useCallback, useRef } from "react";
import { FaInfoCircle, FaCheck } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import TablePagesTitle from "../../../components/table-pages-title/TablePagesTitle";
import AccessPageAction from "../page-action/AccessPageAction";
import { showToast } from "../../../utils/utils";
import {
  DIGIT_REGEX,
  EMAIL_REGEX_STRICT,
  ESCAPE_REGEX,
  NAME_REGEX,
  showToastType,
  UPPERCASE_REGEX
} from "../../../utils/constants/appConstants";
import {
  alertActions,
  buttonName,
  changeUserPass,
  createUser,
  passwordtext,
  profiletexts,
  reset_password,
  signup,
  userManage
} from "../../../locales/en";
import {
  getUserManagementCreateURL,
  getAllRolesUrl
} from "../../../utils/constants/apiEndPoints";
import privateAxios from "../../../utils/axiosPrivate";
import { ADMIN_ACCESS_USER } from "../../../route/paths";

const CreateUser = () => {
  const [firstName, setName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [primaryEmail, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirm] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [isConfirmed, setIsConfirmed] = useState(true);
  const [sendConfirmationEmail, setIsEmail] = useState(false);
  const [roles, setRoles] = useState([]);
  const [roleIds, setSelectedRoles] = useState([]);
  const [nameError, setNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState([]);
  const [passwordConfirmError, setPasswordConfirmError] = useState("");
  const [passwordValid, setPasswordValid] = useState(false);
  const [rolesError, setRolesError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const initialRender = useRef(true);

  const history = useNavigate();

  const fetchRoles = useCallback(async () => {
    try {
      const response = await privateAxios.get(getAllRolesUrl);
      setRoles(response.data.data);
    } catch (error) {
      if (!error.response || error.response.status !== 404) {
        showToast(
          error.response?.data?.message || error.message,
          showToastType.error
        );
      }
    }
  }, [privateAxios]);

  useEffect(() => {
    if (initialRender.current) {
      fetchRoles();
    }
  }, [fetchRoles]);

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) {
      errors.push(signup.passValidation.rule1);
    }
    if (!DIGIT_REGEX.test(password)) {
      errors.push(signup.passValidation.rule2);
    }
    if (!UPPERCASE_REGEX.test(password)) {
      errors.push(signup.passValidation.rule3);
    }
    if (!ESCAPE_REGEX.test(password)) {
      errors.push(signup.passValidation.rule4);
    }
    return errors;
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    setName(value);
  };

  const handleFirstNamelur = (e) => {
    const value = e.target.value;
    const isValidName = NAME_REGEX.test(value);

    if (!isValidName) {
      setNameError(signup.errors.firstnameErr);
      return;
    }

    if (value.length > 0 && value.length < 2) {
      setNameError(profiletexts.errorMessage.firstNameError);
    } else {
      setNameError("");
    }
  };

  const handleMiddleNameChange = (e) => {
    setMiddleName(e.target.value);
  };

  const handleLastNameChange = (e) => {
    const value = e.target.value;
    setLastName(value);
  };

  const handleLastNamelur = (e) => {
    const value = e.target.value;
    const isValidName = NAME_REGEX.test(value);

    if (!isValidName) {
      setLastNameError(signup.errors.lastnameErr);
      return;
    }

    if (value.length > 0 && value.length < 2) {
      setLastNameError(profiletexts.errorMessage.lastNameError);
    } else {
      setLastNameError("");
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
  };

  const handleEmailValidate = (e) => {
    const value = e.target.value;
    const emailRegex = EMAIL_REGEX_STRICT.test(value);
    setEmail(value);

    if (emailRegex) {
      setEmailError("");
    } else {
      setEmailError(createUser.errMsg.item1);
    }
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    const errors = validatePassword(newPassword);
    setPasswordError(errors);
    setPasswordValid(errors.length === 0);
  };

  const handlePasswordBlur = (e) => {
    const newPassword = e.target.value;
    const errors = validatePassword(newPassword);
    setPasswordError(errors);
    setPasswordValid(errors.length === 0);
  };

  const handlePasswordConfirmChange = (e) => {
    setPasswordConfirm(e.target.value);
    setPasswordConfirmError("");
  };

  const handleActiveChange = (e) => {
    setIsActive(e.target.checked);
  };

  const handleConfirmChange = (e) => {
    setIsConfirmed(e.target.checked);
    setIsEmail(false);
  };

  const handleActiveEmailChange = (e) => {
    setIsEmail(e.target.checked);
    setIsConfirmed(false);
  };

  const handleRolesChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (e.target.checked) {
      setSelectedRoles((prevRoleIds) => [...prevRoleIds, value]);
    } else {
      setSelectedRoles((prevRoleIds) =>
        prevRoleIds.filter((id) => id !== value)
      );
    }
  };

  // for submit form
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    let isValid = true;

    if (!firstName) {
      setNameError(createUser.errMsg.item2);
      isValid = false;
    } else if (firstName.length < 2) {
      setNameError(profiletexts.errorMessage.firstNameError);
      isValid = false;
    }

    if (!lastName) {
      setLastNameError(createUser.errMsg.item3);
      isValid = false;
    } else if (lastName.length < 2) {
      setLastNameError(profiletexts.errorMessage.lastNameError);
      isValid = false;
    }

    if (!primaryEmail) {
      setEmailError(createUser.errMsg.item1);
      isValid = false;
    }

    const passwordErrors = validatePassword(password);
    if (passwordErrors.length > 0) {
      setPasswordError(passwordErrors);
      isValid = false;
    }

    if (password !== passwordConfirmation) {
      setPasswordConfirmError(createUser.errMsg.item4);
      isValid = false;
    }

    if (roleIds.length === 0) {
      setRolesError(createUser.errMsg.item5);
      isValid = false;
    } else {
      setRolesError("");
    }

    if (!isValid) {
      return;
    }

    try {
      setIsSubmitting(true);
      const userData = {
        firstName,
        middleName,
        lastName,
        primaryEmail,
        password,
        passwordConfirmation,
        isActive,
        isConfirmed,
        sendConfirmationEmail,
        roleIds
      };

      const response = await privateAxios.post(
        getUserManagementCreateURL,
        userData
      );

      showToast(response.data.message, showToastType.success);

      history(ADMIN_ACCESS_USER);
    } catch (error) {
      if (error.response && error.response.status === 400) {
        const { message, errors } = error.response.data;

        if (errors && errors[0]?.password) {
          showToast(errors[0].password[0], showToastType.error);
        }

        if (errors && errors.length > 0) {
          errors.forEach((fieldError) => {
            if (fieldError.firstName) {
              setNameError(fieldError.firstName[0]);
            }
            if (fieldError.lastName) {
              setLastNameError(fieldError.lastName[0]);
            }
          });
        }

        if (errors && Array.isArray(errors)) {
          const emailError = errors.find((err) =>
            err.toLowerCase().includes("email")
          );
          if (emailError) {
            showToast(emailError, showToastType.error);
          } else {
            errors.forEach((err) => showToast(err, showToastType.error));
          }
        } else if (message) {
          showToast(message, showToastType.error);
        }

        if (Array.isArray(errors)) {
          errors.forEach((err) => {
            Object.keys(err).forEach((key) => {
              if (Array.isArray(err[key])) {
                err[key].forEach((errorMessage) => {
                  showToast(errorMessage, showToastType.error);
                });
              }
            });
          });
        }
      } else {
        showToast(error.message, showToastType.error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setName("");
    setMiddleName("");
    setLastName("");
    setEmail("");
    setPassword("");
    setPasswordConfirm("");
    setNameError("");
    setLastNameError("");
    setEmailError("");
    setPasswordError([]);
    setPasswordConfirmError("");
    setIsActive(true);
    setIsConfirmed(true);
    setIsEmail(false);
    setSelectedRoles([]);

    history(ADMIN_ACCESS_USER);
  };

  return (
    <>
      <TablePagesTitle
        mainTitle={userManage.mainTitle}
        content={userManage.content}
      />
      <AccessPageAction
        subheading={userManage.creteUser}
        alluser="/admin/access/user"
        allUserTitle={userManage.activeUser}
        createuser="/admin/access/user/create"
        createUserTitle={userManage.creteUser}
        deactivate="/admin/access/user/deactivated"
        deactivateTitle={userManage.deactiveUserBtn}
        deleteuser="/admin/access/user/deleted"
        deleteUserTitle={userManage.deleteUser}
      />
      <div className="row mb-3 mx-0">
        <div className="col-md-12 sale-leade-form mt-3">
          <form onSubmit={handleSubmit}>
            <div className="row mb-2">
              <div className="col-sm-4">
                <div className="input-title">
                  <label htmlFor="firstName">{profiletexts.first_name}</label>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="text"
                    placeholder="First Name"
                    name="firstName"
                    onChange={handleNameChange}
                    onBlur={handleFirstNamelur}
                    value={firstName}
                  />
                  {nameError && (
                    <div className="text-danger small">{nameError}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="row mb-2">
              <div className="col-sm-4">
                <div className="input-title">
                  <label htmlFor="middleName">{profiletexts.middle_name}</label>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="text"
                    placeholder="Middle Name"
                    name="middleName"
                    onChange={handleMiddleNameChange}
                    value={middleName}
                  />
                </div>
              </div>
            </div>

            <div className="row mb-2">
              <div className="col-sm-4">
                <div className="input-title">
                  <label htmlFor="last_name">{profiletexts.last_name}</label>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="text"
                    placeholder="Last Name"
                    name="lastName"
                    onChange={handleLastNameChange}
                    onBlur={handleLastNamelur}
                    value={lastName}
                  />
                  {lastNameError && (
                    <div className="text-danger small">{lastNameError}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="row mb-2">
              <div className="col-sm-4">
                <div className="input-title">
                  <label htmlFor="mailAdress">{createUser.mailAdress}</label>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="email"
                    placeholder="E-mail Address"
                    name="primaryEmail"
                    onChange={handleEmailChange}
                    onBlur={handleEmailValidate}
                    value={primaryEmail}
                  />
                  {emailError && (
                    <div className="text-danger small">{emailError}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="row mb-2">
              <div className="col-sm-4">
                <div className="input-title">
                  <label htmlFor="password">{reset_password.main_title2}</label>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="input-group-wrap">
                  <div className="input-group">
                    <input
                      type="password"
                      placeholder="Password"
                      name="password"
                      onChange={handlePasswordChange}
                      onBlur={handlePasswordBlur}
                      value={password}
                      minLength={8}
                    />
                  </div>
                  {passwordError.length > 0
                    ? passwordError.map((error, index) => (
                        <div
                          key={index}
                          className="text-danger small d-block red-color"
                        >
                          <FaInfoCircle /> {error}
                        </div>
                      ))
                    : passwordValid && (
                        <div className="small primary-color">
                          <FaCheck /> {passwordtext?.okPassword}
                        </div>
                      )}
                </div>
              </div>
            </div>

            <div className="row mb-2">
              <div className="col-sm-4">
                <div className="input-title">
                  <label htmlFor="passowrdConfirm">
                    {changeUserPass.passowrdConfirm}
                  </label>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="password"
                    placeholder="Password Confirmation"
                    name="passwordConfirmation"
                    onChange={handlePasswordConfirmChange}
                    value={passwordConfirmation}
                  />
                  {passwordConfirmError && (
                    <div className="text-danger small">
                      {passwordConfirmError}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="row mb-2">
              <div className="col-sm-4">
                <div className="input-title">
                  <label htmlFor="activeDeactive">
                    {createUser.activeDeactive}
                  </label>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={isActive}
                    onChange={handleActiveChange}
                  />
                </div>
              </div>
            </div>

            <div className="row mb-2">
              <div className="col-sm-4">
                <div className="input-title">
                  <label htmlFor="confirmed">{createUser.confirmed} </label>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="checkbox"
                    name="isConfirmed"
                    checked={isConfirmed}
                    onChange={handleConfirmChange}
                  />
                </div>
              </div>
            </div>

            <div className="row mb-2">
              <div className="col-sm-4">
                <div className="input-title">
                  <label htmlFor="sendConfirmEmail">
                    {createUser.sendConfirmEmail}
                  </label>
                  <br />
                  <small className="fontWeight400">
                    {createUser.sendEmailText}
                  </small>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="checkbox"
                    name="sendConfirmationEmail"
                    checked={sendConfirmationEmail}
                    onChange={handleActiveEmailChange}
                  />
                </div>
              </div>
            </div>

            <div className="row mb-2">
              <div className="col-sm-4">
                <div className="input-title">
                  <label htmlFor="associatedRoles">
                    {createUser.associatedRoles}
                  </label>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="border p-3">
                  <div className="row role-list-wrap">
                    {roles.map((role) => (
                      <div key={role.id} className="col-lg-6 col-xl-4">
                        <input
                          type="checkbox"
                          id={role.id}
                          name="role"
                          value={role.id}
                          onChange={handleRolesChange}
                          checked={roleIds.includes(parseInt(role.id))}
                        />
                        <label className="ms-1" htmlFor={role.id}>
                          {role.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                {rolesError && (
                  <div className="text-danger small">{rolesError}</div>
                )}
              </div>
            </div>

            <div className="row mt-4">
              <div className="col-sm-8 offset-sm-4 d-flex d-sm-block justify-content-center justify-content-sm-start">
                <button
                  type="button"
                  className="btn btn-red me-2"
                  onClick={handleCancel}
                >
                  {alertActions.Cancel}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {buttonName.create}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default CreateUser;
